import { useCallback, useRef, useState } from 'react'
import { vorbelegung } from './Zeitraum'
import type { Raster } from './Zeitraum'

/**
 * **Die Auswahl bleibt stehen** (7.2 Abschnitt 44, R1363).
 *
 * Der Befund des Auftraggebers vom 2026-10-06: *„CoKo merkt sich meine
 * Ansichten nicht. Ständig wird der Konzern ausgewählt."* Jede Auswahl hielt
 * nur, solange das Fenster offen war; nach dem Neuladen stand wieder der erste
 * Eintrag da.
 *
 * **Eine Stelle für die ganze Anwendung** — wer eine Gesellschaft, eine
 * Bewertungsebene oder einen Zeitraum wählen lässt, nimmt diese hier. Der
 * Prüffall `Gemerkt.test.ts` meldet jede Auswahl, die es nicht tut (R1367).
 *
 * Gemerkt wird **im Browser** und **die letzte Wahl**: Wer im zweiten Fenster
 * wechselt, findet das beim nächsten Öffnen wieder; ein offenes Fenster ändert
 * sich dadurch nicht (R530).
 */
const ABLAGE = 'coko.wahl.'

/**
 * Was zu einer Kennung gemerkt ist — `undefined`, wenn nichts oder nichts
 * Lesbares. **Ein gesperrter Speicher ist kein Fehler**, sondern kein Wert.
 */
export function liesWahl(kennung: string): unknown {
  try {
    const roh = window.localStorage.getItem(ABLAGE + kennung)
    return roh === null ? undefined : (JSON.parse(roh) as unknown)
  } catch {
    return undefined
  }
}

export function merkeWahl(kennung: string, wert: unknown): void {
  try {
    window.localStorage.setItem(ABLAGE + kennung, JSON.stringify(wert))
  } catch {
    // Gesperrter Speicher: Die Wahl gilt in diesem Fenster und wird nicht gemerkt.
  }
}

type Wahlwert = string | number | null

/** Setzt die Wahl — wie bei `useState` auch aus dem jetzigen Wert. */
type Setzer<T> = (neu: T | ((jetzt: T) => T)) => void

/** Passt das Gemerkte zur Art der Vorgabe? Ein fremder Wert ist keiner. */
function passend<T extends Wahlwert>(gemerkt: unknown, vorgabe: T): gemerkt is T {
  // Leer als Vorgabe heißt bisher immer: eine Periode oder keine.
  if (vorgabe === null) {
    return gemerkt === null || typeof gemerkt === 'number'
  }
  return typeof gemerkt === typeof vorgabe
}

function lies<T extends Wahlwert>(kennung: string, vorgabe: T): T {
  const gemerkt = liesWahl(kennung)
  return passend(gemerkt, vorgabe) ? gemerkt : vorgabe
}

/**
 * Wie `useState`, nur dass die Wahl stehen bleibt.
 *
 * **Ob der Wert noch zur Wahl steht, prüft der Ort** (R1364): Nur er kennt
 * seine Liste. Steht das Gemerkte dort nicht mehr — eine stillgelegte
 * Gesellschaft —, setzt er die Vorgabe, und die gilt dann auch als gemerkt.
 *
 * **Die Kennung darf wechseln** — das Partnerbild merkt je Kreis. Dann gilt,
 * was zur neuen Kennung gemerkt ist.
 */
export function useGemerkt(kennung: string, vorgabe: string): [string, Setzer<string>]
export function useGemerkt(kennung: string, vorgabe: number | null): [number | null, Setzer<number | null>]
export function useGemerkt<T extends Wahlwert>(kennung: string, vorgabe: T): [T, Setzer<T>] {
  const [stand, setStand] = useState(() => ({ kennung, wert: lies(kennung, vorgabe) }))

  let wert = stand.wert
  if (stand.kennung !== kennung) {
    wert = lies(kennung, vorgabe)
    setStand({ kennung, wert })
  }

  // Der neueste Wert, auch zwischen zwei Bildern — für die Wahl aus dem jetzigen.
  const aktuell = useRef(wert)
  aktuell.current = wert

  const setze = useCallback(
    (neu: T | ((jetzt: T) => T)) => {
      const gewaehlt = typeof neu === 'function' ? neu(aktuell.current) : neu
      aktuell.current = gewaehlt
      setStand({ kennung, wert: gewaehlt })
      merkeWahl(kennung, gewaehlt)
    },
    [kennung],
  )

  return [wert, setze]
}

/** Der Zeitraum über dem Bericht: Anfang und Ende als Monat, dazu das Raster. */
export type Zeitraumwahl = { von: number; bis: number; raster: Raster }

/** Ein Zeitraum aus fremder Quelle — Adresse oder Speicher —, `null`, wenn er keiner ist. */
export function alsZeitraum(roh: unknown): Zeitraumwahl | null {
  const z = roh as Partial<Record<keyof Zeitraumwahl, unknown>> | null | undefined
  if (!z || typeof z.von !== 'number' || typeof z.bis !== 'number' || !(z.bis > 0)) {
    return null
  }
  const raster: Raster = z.raster === 'quartal' || z.raster === 'jahr' ? z.raster : 'monat'
  return { von: z.von, bis: z.bis, raster }
}

/**
 * **Womit der Zeitraum anfängt** (R530, R1363, R1364): der erste Wunsch,
 * dessen Ende Zahlen hat — sonst das Geschäftsjahr bis zum jüngsten Lauf, im
 * Monatsraster. `gemerkt` sagt, ob der gemerkte gewonnen hat; nur dann kann
 * der Verweis auf neuere Zahlen stehen (R1365).
 */
export function anfangszeitraum(
  liste: readonly number[],
  mitgegeben: Zeitraumwahl | null,
  gemerkt: Zeitraumwahl | null,
): { zeitraum: Zeitraumwahl | null; gemerkt: boolean } {
  if (mitgegeben && liste.includes(mitgegeben.bis)) {
    return { zeitraum: mitgegeben, gemerkt: false }
  }
  if (gemerkt && liste.includes(gemerkt.bis)) {
    return { zeitraum: gemerkt, gemerkt: true }
  }
  const vorgabe = vorbelegung(liste)
  return { zeitraum: vorgabe ? { ...vorgabe, raster: 'monat' } : null, gemerkt: false }
}

/** Der jüngste Monat mit Zahlen, wenn er hinter `bis` liegt — sonst `null` (R1365). */
export function neuereZahlen(liste: readonly number[], bis: number): number | null {
  const juengste = liste.length > 0 ? Math.max(...liste) : 0
  return juengste > bis ? juengste : null
}
